import { Injectable, Injector, NgZone, OnDestroy, inject } from '@angular/core';
import {
  ActionSheetController,
  AlertController,
  ModalController,
  ModalOptions,
  PickerController,
  PopoverController,
} from '@ionic/angular/standalone';
import {
  readStateHistoryPageId,
  StateHistoryTraversalResult,
  traverseStateHistory,
} from '@state-routing/services/state-history-guard';
import { StateMenuLifecycle } from '@state-routing/services/state-menu-lifecycle';

type StateOverlayElement = HTMLIonActionSheetElement
  | HTMLIonAlertElement
  | HTMLIonModalElement
  | HTMLIonPickerElement
  | HTMLIonPopoverElement;

type StateNavigationLayerElement = StateOverlayElement | HTMLIonMenuElement;
type StateOverlayHistoryMode = 'current' | 'entry';

interface StateManagedOverlayEntry {
  readonly close: (data?: unknown, role?: string) => Promise<boolean>;
  readonly element: StateNavigationLayerElement;
  readonly historyMode: StateOverlayHistoryMode;
  readonly historyPageId: number | null;
  readonly id: string;
  readonly settled: Promise<void>;
  readonly url: string;
  historyEntryActive: boolean;
  resolveSettled: () => void;
}

@Injectable()
export class StateOverlayService implements OnDestroy {
  private readonly injector = inject(Injector);
  private readonly ngZone = inject(NgZone);

  private static readonly HISTORY_STATE_KEY: string = 'stateOverlayIds';
  private static readonly MAX_STALE_HISTORY_POP_COUNT: number = 16;
  private static readonly WILL_PRESENT_EVENTS: readonly string[] = [
    'ionActionSheetWillPresent',
    'ionAlertWillPresent',
    'ionModalWillPresent',
    'ionPickerWillPresent',
    'ionPopoverWillPresent',
  ];

  private readonly managedEntries: StateManagedOverlayEntry[] = [];
  private readonly managedEntriesByElement = new Map<StateNavigationLayerElement, StateManagedOverlayEntry>();
  private readonly browserBackDismissingEntries = new Set<StateManagedOverlayEntry>();
  private readonly dismissingEntries = new Set<StateManagedOverlayEntry>();
  private readonly finalizingEntries = new Set<StateManagedOverlayEntry>();
  private readonly currentHistoryElements = new WeakSet<StateOverlayElement>();
  private readonly menuLifecycles = new Map<HTMLIonMenuElement, StateMenuLifecycle>();
  private forwardRejectionOperation: Promise<void> | null = null;
  private historyOperationTail: Promise<void> = Promise.resolve();
  private sequence: number = 0;
  private started: boolean = false;

  private readonly handleOverlayWillPresent = (event: Event): void => {
    if (this.isStateOverlayElement(event.target)) {
      this.register(event.target);
    }
  };

  ngOnDestroy(): void {
    if (typeof document !== 'undefined') {
      StateOverlayService.WILL_PRESENT_EVENTS.forEach((eventName: string): void => {
        document.removeEventListener(eventName, this.handleOverlayWillPresent, true);
      });
    }
    this.menuLifecycles.forEach((lifecycle: StateMenuLifecycle): void => lifecycle.destroy());
    this.menuLifecycles.clear();
    this.managedEntriesByElement.clear();
    this.dismissingEntries.clear();
    this.finalizingEntries.clear();
    this.started = false;
  }

  public start(): void {
    if (this.started || typeof window === 'undefined') return;

    this.started = true;
    if (typeof document !== 'undefined') {
      StateOverlayService.WILL_PRESENT_EVENTS.forEach((eventName: string): void => {
        document.addEventListener(eventName, this.handleOverlayWillPresent, true);
      });
    }
    void this.initializeHistoryAndPresentedOverlays();
  }

  public async openModal(options: ModalOptions): Promise<HTMLIonModalElement> {
    const modal: HTMLIonModalElement = await this.injector.get(ModalController).create(options);
    await modal.present();
    this.register(modal);
    return modal;
  }

  public registerMenu(element: EventTarget | null): void {
    if (!this.isMenuElement(element)) return;

    const currentLifecycle: StateMenuLifecycle | undefined = this.menuLifecycles.get(element);
    if (currentLifecycle && !currentLifecycle.isClosed) return;

    const lifecycle = new StateMenuLifecycle(element);
    this.menuLifecycles.set(element, lifecycle);
    void lifecycle.didClose.then((): void => {
      if (this.menuLifecycles.get(element) === lifecycle) this.menuLifecycles.delete(element);
    });

    const previousEntry: StateManagedOverlayEntry | undefined = this.managedEntriesByElement.get(element);
    if (previousEntry) {
      void previousEntry.settled.then((): void => this.registerMenuLayer(element, lifecycle));
    } else {
      this.registerMenuLayer(element, lifecycle);
    }
  }

  public async presentInCurrentHistoryEntry(element: StateOverlayElement): Promise<void> {
    this.currentHistoryElements.add(element);
    await element.present();
    this.register(element);
  }

  public async waitUntilSettled(element: StateOverlayElement): Promise<void> {
    const entry: StateManagedOverlayEntry | undefined = this.managedEntriesByElement.get(element);
    if (entry) {
      await entry.settled;
    }
  }

  public interceptBrowserPopState(event: PopStateEvent): boolean {
    const activeIds: readonly string[] = this.readHistoryIds(event.state);
    const topEntry: StateManagedOverlayEntry | undefined = this.resolveTopManagedEntry();
    if (topEntry?.historyMode === 'current') {
      const restorationDelta: number | null = this.resolveCurrentHistoryRestorationDelta(topEntry, event.state);
      if (restorationDelta === null) return false;
      if (restorationDelta === 0) return this.isCurrentHistoryEntry(topEntry);

      this.ngZone.run((): void => {
        void this.enqueueHistoryOperation(
          (): Promise<void> => this.restoreCurrentHistoryEntry(topEntry, restorationDelta),
        );
      });
      return true;
    }
    if (topEntry && !activeIds.includes(topEntry.id)) {
      this.ngZone.run((): void => {
        this.browserBackDismissingEntries.add(topEntry);
        void this.dismissFromBrowserBack(topEntry);
      });
      return true;
    }

    const knownIds: ReadonlySet<string> = this.resolveManagedHistoryIds();
    const staleIds = new Set<string>(activeIds.filter((id: string): boolean => !knownIds.has(id)));
    if (staleIds.size > 0) {
      this.ngZone.run((): void => {
        void this.rejectStaleOverlayForward(staleIds);
      });
      return true;
    }
    return false;
  }

  public async closeTop(): Promise<boolean> {
    return await this.closeTopLayer(false);
  }

  public async closeAll(): Promise<boolean> {
    while (await this.hasOpenOverlay()) {
      if (!await this.closeTopLayer(true) && await this.hasOpenOverlay()) return false;
    }
    return true;
  }

  private register(element: StateOverlayElement): void {
    this.registerLayer(
      element,
      (data?: unknown, role?: string): Promise<boolean> => element.dismiss(data, role),
      element.onWillDismiss(),
      element.onDidDismiss(),
      this.currentHistoryElements.has(element) ? 'current' : 'entry',
    );
  }

  private registerMenuLayer(element: HTMLIonMenuElement, lifecycle: StateMenuLifecycle): void {
    if (this.menuLifecycles.get(element) !== lifecycle || lifecycle.isClosed) return;

    this.registerLayer(
      element,
      (): Promise<boolean> => lifecycle.close(),
      null,
      lifecycle.didClose,
      'entry',
    );
  }

  private async closeTopLayer(closeCurrentHistoryEntry: boolean): Promise<boolean> {
    const managedEntry: StateManagedOverlayEntry | undefined = this.resolveTopManagedEntry();
    const unmanagedOverlay: StateOverlayElement | null = await this.resolveTopUnmanagedOverlay();
    if (
      unmanagedOverlay
      && (
        !managedEntry
        || this.resolveZIndex(unmanagedOverlay) >= this.resolveZIndex(managedEntry.element)
      )
    ) {
      return await unmanagedOverlay.dismiss(undefined, 'state-back');
    }

    if (managedEntry) {
      if (managedEntry.historyMode === 'current' && !closeCurrentHistoryEntry) return true;

      if (this.dismissingEntries.has(managedEntry)) {
        await managedEntry.settled;
        return true;
      }

      const dismissed: boolean = await managedEntry.close(undefined, 'state-back');
      if (!dismissed) {
        if (!this.dismissingEntries.has(managedEntry)) return false;

        await managedEntry.settled;
        return true;
      }

      await managedEntry.settled;
      return true;
    }
    const finalizingEntries: readonly StateManagedOverlayEntry[] = [...this.finalizingEntries];
    const finalizingEntry: StateManagedOverlayEntry | undefined = finalizingEntries[finalizingEntries.length - 1];
    if (finalizingEntry) {
      await finalizingEntry.settled;
      return true;
    }
    return false;
  }

  private registerLayer(
    element: StateNavigationLayerElement,
    close: (data?: unknown, role?: string) => Promise<boolean>,
    willDismiss: Promise<unknown> | null,
    didDismiss: Promise<unknown>,
    historyMode: StateOverlayHistoryMode,
  ): void {
    if (this.managedEntriesByElement.has(element)) return;

    let resolveSettled: () => void = (): void => undefined;
    const settled: Promise<void> = new Promise<void>((resolve): void => {
      resolveSettled = resolve;
    });
    const entry: StateManagedOverlayEntry = {
      close,
      element,
      historyMode,
      historyPageId: typeof history === 'undefined' ? null : this.readHistoryPageId(history.state),
      historyEntryActive: false,
      id: `state-overlay-${++this.sequence}`,
      settled,
      url: typeof window === 'undefined' ? '' : window.location.href,
      resolveSettled,
    };
    this.managedEntries.push(entry);
    this.managedEntriesByElement.set(element, entry);
    if (entry.historyMode === 'entry') {
      void this.enqueueHistoryOperation((): void => {
        if (!this.managedEntries.includes(entry) || this.dismissingEntries.has(entry)) return;

        entry.historyEntryActive = true;
        this.pushHistoryEntry();
      });
    }
    void willDismiss?.then((): void => {
      this.dismissingEntries.add(entry);
    });
    void didDismiss.then((): Promise<void> => this.finalizeDismiss(entry));
  }

  private async hasOpenOverlay(): Promise<boolean> {
    return this.managedEntries.length > 0
      || this.finalizingEntries.size > 0
      || await this.resolveTopUnmanagedOverlay() !== null;
  }

  private async finalizeDismiss(entry: StateManagedOverlayEntry): Promise<void> {
    const wasTopManagedEntry: boolean = this.managedEntries[this.managedEntries.length - 1] === entry;
    this.finalizingEntries.add(entry);
    this.browserBackDismissingEntries.delete(entry);
    this.dismissingEntries.delete(entry);
    const index: number = this.managedEntries.indexOf(entry);
    if (index >= 0) {
      this.managedEntries.splice(index, 1);
    }

    try {
      await this.enqueueHistoryOperation(async (): Promise<void> => {
        if (entry.historyMode !== 'entry' || !entry.historyEntryActive) return;

        if (this.readCurrentHistoryIds().includes(entry.id)) {
          if (wasTopManagedEntry) {
            await this.consumeHistoryEntry(entry.id);
          } else {
            this.removeHistoryIds(new Set<string>([entry.id]));
          }
        }
        entry.historyEntryActive = false;
        await this.consumeCurrentStaleHistoryEntries();
      });
    } finally {
      entry.resolveSettled();
      this.finalizingEntries.delete(entry);
      this.managedEntriesByElement.delete(entry.element);
    }
  }

  private pushHistoryEntry(): void {
    if (typeof history === 'undefined' || typeof window === 'undefined') return;

    const state: Record<string, unknown> = this.copyHistoryState(history.state);
    state[StateOverlayService.HISTORY_STATE_KEY] = this.managedEntries
      .filter((entry: StateManagedOverlayEntry): boolean => (
        entry.historyMode === 'entry'
        && entry.historyEntryActive
        && !this.browserBackDismissingEntries.has(entry)
      ))
      .map(
        (entry: StateManagedOverlayEntry): string => entry.id,
      );
    history.pushState(state, '', window.location.href);
  }

  private async dismissFromBrowserBack(entry: StateManagedOverlayEntry): Promise<void> {
    const dismissed: boolean = await entry
      .close(undefined, 'browser-back')
      .catch((): boolean => false);
    if (dismissed || this.dismissingEntries.has(entry)) {
      await entry.settled;
      return;
    }

    this.browserBackDismissingEntries.delete(entry);
    if (this.managedEntries.includes(entry)) {
      await this.enqueueHistoryOperation((): void => this.pushHistoryEntry());
    }
  }

  private async consumeHistoryEntry(overlayId: string): Promise<void> {
    const traversalResult: StateHistoryTraversalResult = await traverseStateHistory(
      -1,
      (): boolean => !this.readCurrentHistoryIds().includes(overlayId),
    );
    if (traversalResult === 'expected') return;

    console.warn('[StateOverlay] browser history did not consume a dismissed overlay entry', {
      overlayId,
      traversalResult,
    });
    this.removeHistoryIds(new Set<string>([overlayId]));
  }

  private async consumeCurrentStaleHistoryEntries(): Promise<void> {
    const knownIds: ReadonlySet<string> = this.resolveManagedHistoryIds();
    const staleIds = new Set<string>(this.readCurrentHistoryIds().filter(
      (id: string): boolean => !knownIds.has(id),
    ));
    if (staleIds.size === 0) return;

    await this.consumeStaleHistoryEntries(staleIds, 'overlay dismiss');
  }

  private async restoreCurrentHistoryEntry(
    entry: StateManagedOverlayEntry,
    delta: number,
  ): Promise<void> {
    const traversalResult: StateHistoryTraversalResult = await traverseStateHistory(
      delta,
      (): boolean => this.isCurrentHistoryEntry(entry),
    );
    if (traversalResult === 'expected') return;

    console.warn('[StateOverlay] failed to restore a guarded overlay history entry', {
      delta,
      overlayId: entry.id,
      traversalResult,
    });
  }

  private async rejectStaleOverlayForward(staleIds: ReadonlySet<string>): Promise<void> {
    if (this.forwardRejectionOperation) {
      await this.forwardRejectionOperation;
      return;
    }

    const operation: Promise<void> = this.enqueueHistoryOperation(
      (): Promise<void> => this.performStaleOverlayForwardRejection(staleIds),
    );
    this.forwardRejectionOperation = operation;
    try {
      await operation;
    } finally {
      if (this.forwardRejectionOperation === operation) {
        this.forwardRejectionOperation = null;
      }
    }
  }

  private async performStaleOverlayForwardRejection(staleIds: ReadonlySet<string>): Promise<void> {
    await this.consumeStaleHistoryEntries(staleIds, 'browser Forward');
  }

  private async initializeHistoryAndPresentedOverlays(): Promise<void> {
    await this.enqueueHistoryOperation((): Promise<void> => this.removeStaleHistoryState());
    await this.registerPresentedOverlays();
  }

  private enqueueHistoryOperation(action: () => void | Promise<void>): Promise<void> {
    const operation: Promise<void> = this.historyOperationTail.then(action, action);
    this.historyOperationTail = operation.catch((error: unknown): void => {
      console.error('[StateOverlay] history operation failed', error);
    });
    return operation;
  }

  private async removeStaleHistoryState(): Promise<void> {
    if (typeof history === 'undefined' || typeof window === 'undefined') return;
    const staleIds: readonly string[] = this.readCurrentHistoryIds();
    if (!staleIds.length) return;

    await this.consumeStaleHistoryEntries(new Set<string>(staleIds), 'startup');
  }

  private async consumeStaleHistoryEntries(
    staleIds: ReadonlySet<string>,
    source: string,
  ): Promise<void> {
    for (let popCount: number = 0; popCount < StateOverlayService.MAX_STALE_HISTORY_POP_COUNT; popCount += 1) {
      if (!this.hasCurrentHistoryIds(staleIds)) return;

      const traversalResult: StateHistoryTraversalResult = await traverseStateHistory(-1, (): boolean => true);
      if (traversalResult === 'expected') continue;

      console.warn('[StateOverlay] failed to consume a stale overlay history entry', {
        source,
        traversalResult,
      });
      this.removeHistoryIds(staleIds);
      return;
    }

    console.warn('[StateOverlay] stale overlay history exceeded the traversal limit', {source});
    this.removeHistoryIds(staleIds);
  }

  private hasCurrentHistoryIds(ids: ReadonlySet<string>): boolean {
    return this.readCurrentHistoryIds().some((id: string): boolean => ids.has(id));
  }

  private removeHistoryIds(idsToRemove: ReadonlySet<string>): void {
    if (typeof history === 'undefined' || typeof window === 'undefined') return;

    const state: Record<string, unknown> = this.copyHistoryState(history.state);
    const remainingIds: readonly string[] = this.readHistoryIds(state).filter(
      (id: string): boolean => !idsToRemove.has(id),
    );
    if (remainingIds.length) {
      state[StateOverlayService.HISTORY_STATE_KEY] = remainingIds;
    } else {
      delete state[StateOverlayService.HISTORY_STATE_KEY];
    }
    history.replaceState(state, '', window.location.href);
  }

  private readCurrentHistoryIds(): readonly string[] {
    return typeof history === 'undefined' ? [] : this.readHistoryIds(history.state);
  }

  private resolveManagedHistoryIds(): ReadonlySet<string> {
    return new Set<string>(this.managedEntries
      .filter((entry: StateManagedOverlayEntry): boolean => (
        entry.historyMode === 'entry' && entry.historyEntryActive
      ))
      .map((entry: StateManagedOverlayEntry): string => entry.id));
  }

  private readHistoryIds(state: unknown): readonly string[] {
    if (!this.isRecord(state)) return [];

    const ids: unknown = state[StateOverlayService.HISTORY_STATE_KEY];
    return Array.isArray(ids)
      ? ids.filter((id: unknown): id is string => typeof id === 'string')
      : [];
  }

  private readHistoryPageId(state: unknown): number | null {
    return readStateHistoryPageId(state);
  }

  private resolveCurrentHistoryRestorationDelta(
    entry: StateManagedOverlayEntry,
    targetState: unknown,
  ): number | null {
    if (entry.historyPageId === null) return null;

    const targetPageId: number | null = this.readHistoryPageId(targetState);
    return targetPageId === null ? null : entry.historyPageId - targetPageId;
  }

  private isCurrentHistoryEntry(entry: StateManagedOverlayEntry): boolean {
    if (typeof history === 'undefined' || typeof window === 'undefined') return false;

    return window.location.href === entry.url
      && this.readHistoryPageId(history.state) === entry.historyPageId;
  }

  private copyHistoryState(state: unknown): Record<string, unknown> {
    return this.isRecord(state) ? {...state} : {};
  }

  private resolveTopManagedEntry(): StateManagedOverlayEntry | undefined {
    for (let index: number = this.managedEntries.length - 1; index >= 0; index--) {
      const entry: StateManagedOverlayEntry = this.managedEntries[index];
      if (!this.browserBackDismissingEntries.has(entry)) return entry;
    }
    return undefined;
  }

  private async resolveTopUnmanagedOverlay(): Promise<StateOverlayElement | null> {
    const overlays: StateOverlayElement[] = await this.resolveUnmanagedOverlays();
    if (!overlays.length) return null;

    return overlays.reduce((top: StateOverlayElement, candidate: StateOverlayElement): StateOverlayElement => (
      this.resolveZIndex(candidate) > this.resolveZIndex(top) ? candidate : top
    ));
  }

  private async registerPresentedOverlays(): Promise<void> {
    const overlays: StateOverlayElement[] = await this.resolveUnmanagedOverlays();
    overlays
      .sort((first: StateOverlayElement, second: StateOverlayElement): number => (
        this.resolveZIndex(first) - this.resolveZIndex(second)
      ))
      .forEach((element: StateOverlayElement): void => this.register(element));
  }

  private async resolveUnmanagedOverlays(): Promise<StateOverlayElement[]> {
    const overlays: StateOverlayElement[] = [];
    const actionSheet: HTMLIonActionSheetElement | undefined = await this.injector.get(ActionSheetController).getTop();
    const alert: HTMLIonAlertElement | undefined = await this.injector.get(AlertController).getTop();
    const modal: HTMLIonModalElement | undefined = await this.injector.get(ModalController).getTop();
    const picker: HTMLIonPickerElement | undefined = await this.injector.get(PickerController).getTop();
    const popover: HTMLIonPopoverElement | undefined = await this.injector.get(PopoverController).getTop();
    [actionSheet, alert, modal, picker, popover].forEach((element: StateOverlayElement | undefined): void => {
      if (element && !this.managedEntries.some(
        (entry: StateManagedOverlayEntry): boolean => entry.element === element,
      )) {
        overlays.push(element);
      }
    });
    return overlays;
  }

  private resolveZIndex(element: StateNavigationLayerElement): number {
    if (typeof window === 'undefined') return 0;
    const zIndex: number = Number(window.getComputedStyle(element).zIndex);
    return Number.isFinite(zIndex) ? zIndex : 0;
  }

  private isRecord(value: unknown): value is Record<string, unknown> {
    return value !== null && typeof value === 'object' && !Array.isArray(value);
  }

  private isStateOverlayElement(value: EventTarget | null): value is StateOverlayElement {
    return value instanceof HTMLElement
      && (
        value.tagName === 'ION-ACTION-SHEET'
        || value.tagName === 'ION-ALERT'
        || value.tagName === 'ION-MODAL'
        || value.tagName === 'ION-PICKER'
        || value.tagName === 'ION-POPOVER'
      );
  }

  private isMenuElement(value: EventTarget | null): value is HTMLIonMenuElement {
    return value instanceof HTMLElement && value.tagName === 'ION-MENU';
  }
}
